<div>
  <!-- Blockchains Table -->
  <p-table
    #table
    [scrollable]="true"
    scrollHeight="flex"
    [value]="blockchains()"
    [loading]="loading()"
    [paginator]="true"
    [rows]="10"
    [showCurrentPageReport]="true"
    [rowsPerPageOptions]="[10, 25, 50]"
    currentPageReportTemplate="{first}-{last} of {totalRecords}"
    [stripedRows]="true"
  >
    <!-- Table Caption with Search and Add Button -->
    <ng-template pTemplate="caption">
      <div class="flex flex-col md:flex-row gap-3 md:gap-2">
        <div class="flex gap-2">
          <p-button
            label="{{ 'blockchains.addBlockchain' | translate }}"
            icon="pi pi-plus"
            severity="success"
            [disabled]="loading()"
            [loading]="loading()"
            (onClick)="openCreateDialog()"
          >
          </p-button>
        </div>
        <p-iconfield iconPosition="left" class="md:ml-auto w-full md:w-auto">
          <p-inputicon>
            <i class="pi pi-search"></i>
          </p-inputicon>
          <input
            #searchInput
            pInputText
            type="text"
            [formControl]="searchControl"
            [placeholder]="'blockchains.searchPlaceholder' | translate"
            class="w-full"
          />
        </p-iconfield>
      </div>
    </ng-template>

    <!-- Table Header -->
    <ng-template pTemplate="header">
      <tr>
        <th style="min-width: 200px">{{ 'blockchains.headers.label' | translate }}</th>
        <th style="min-width: 300px">{{ 'blockchains.headers.description' | translate }}</th>
        <th style="min-width: 150px">{{ 'blockchains.headers.actions' | translate }}</th>
      </tr>
    </ng-template>

    <!-- Table Body -->
    <ng-template pTemplate="body" let-blockchain>
      <tr>
        <td style="min-width: 200px">{{ blockchain.label }}</td>
        <td style="min-width: 300px">{{ blockchain.description || '-' }}</td>
        <td style="min-width: 150px" class="py-2">
          <div class="flex gap-2">
            <p-button
              icon="pi pi-pencil"
              [rounded]="true"
              size="small"
              [disabled]="loading()"
              [ariaLabel]="'blockchains.edit' | translate"
              (onClick)="openEditDialog(blockchain)"
            >
            </p-button>
            <p-button
              icon="pi pi-trash"
              [rounded]="true"
              size="small"
              [disabled]="loading() || (blockchain.projectCount ?? 0) > 0"
              [ariaLabel]="'blockchains.delete' | translate"
              (onClick)="deleteBlockchain(blockchain)"
              [pTooltip]="
                (blockchain.projectCount ?? 0) > 0
                  ? ('blockchains.inUseCannotDelete' | translate)
                  : ''
              "
              tooltipPosition="top"
            >
            </p-button>
          </div>
        </td>
      </tr>
    </ng-template>

    <!-- Empty state -->
    <ng-template pTemplate="emptymessage">
      <tr>
        <td colspan="3" class="text-center py-8">
          <div class="flex flex-col items-center gap-3">
            <i class="pi pi-link text-4xl text-muted"></i>
            <p class="text-body-md text-muted">{{ 'blockchains.noData' | translate }}</p>
          </div>
        </td>
      </tr>
    </ng-template>
  </p-table>

  <!-- Create/Edit Dialog -->
  <p-dialog
    [header]="getDialogTitle()"
    [(visible)]="dialogVisible"
    [modal]="true"
    [style]="{ width: '500px' }"
    [draggable]="false"
    [resizable]="false"
  >
    <form [formGroup]="blockchainForm" (ngSubmit)="saveBlockchain()">
      <!-- Label Field -->
      <div class="mb-4">
        <label for="label" class="block mb-2">{{ 'blockchains.form.label' | translate }} *</label>
        <input
          id="label"
          type="text"
          pInputText
          formControlName="label"
          [class.ng-invalid]="isFieldInvalid('label')"
          [class.ng-dirty]="isFieldInvalid('label')"
          class="w-full"
          [placeholder]="'blockchains.form.labelPlaceholder' | translate"
        />
        @if (isFieldInvalid('label')) {
        <p-message severity="error">
          {{ getFieldError('label') }}
        </p-message>
        }
        <small class="block mt-1 text-muted">{{ 'blockchains.form.labelHint' | translate }}</small>
      </div>

      <!-- Description Field -->
      <div class="mb-4">
        <label for="description" class="block mb-2">
          {{ 'blockchains.form.description' | translate }}
        </label>
        <textarea
          id="description"
          formControlName="description"
          class="p-inputtext w-full"
          [placeholder]="'blockchains.form.descriptionPlaceholder' | translate"
          rows="3"
        ></textarea>
      </div>

      <!-- Chain ID Field -->
      <div class="mb-4">
        <label for="chainId" class="block mb-2">
          {{ 'blockchains.form.chainId' | translate }}
        </label>
        <input
          id="chainId"
          type="number"
          pInputText
          formControlName="chainId"
          class="w-full"
          [placeholder]="'blockchains.form.chainIdPlaceholder' | translate"
        />
        <small class="block mt-1 text-muted">{{
          'blockchains.form.chainIdHint' | translate
        }}</small>
      </div>

      <!-- EVM Checkbox -->
      <div class="mb-4 flex items-center gap-2">
        <p-checkbox inputId="isEVM" formControlName="isEVM" [binary]="true"></p-checkbox>
        <label for="isEVM" class="cursor-pointer">
          {{ 'blockchains.form.isEVM' | translate }}
        </label>
      </div>

      <!-- RPC URL Field -->
      <div class="mb-4">
        <label for="rpcUrl" class="block mb-2">
          {{ 'blockchains.form.rpcUrl' | translate }}
        </label>
        <input
          id="rpcUrl"
          type="text"
          pInputText
          formControlName="rpcUrl"
          class="w-full"
          [placeholder]="'blockchains.form.rpcUrlPlaceholder' | translate"
        />
      </div>

      <!-- Explorer URL Field -->
      <div class="mb-4">
        <label for="explorerUrl" class="block mb-2">
          {{ 'blockchains.form.explorerUrl' | translate }}
        </label>
        <input
          id="explorerUrl"
          type="text"
          pInputText
          formControlName="explorerUrl"
          class="w-full"
          [placeholder]="'blockchains.form.explorerUrlPlaceholder' | translate"
        />
      </div>

      <!-- Explorer API URL Field -->
      <div class="mb-4">
        <label for="explorerApiUrl" class="block mb-2">
          {{ 'blockchains.form.explorerApiUrl' | translate }}
        </label>
        <input
          id="explorerApiUrl"
          type="text"
          pInputText
          formControlName="explorerApiUrl"
          class="w-full"
          [placeholder]="'blockchains.form.explorerApiUrlPlaceholder' | translate"
        />
        <small class="block mt-1 text-muted">{{
          'blockchains.form.explorerApiUrlHint' | translate
        }}</small>
      </div>

      <!-- Dialog Footer -->
      <div class="flex justify-end gap-2 mt-6">
        <p-button
          label="{{ 'common.cancel' | translate }}"
          severity="secondary"
          (onClick)="dialogVisible.set(false)"
          [disabled]="loading()"
        >
        </p-button>
        <p-button
          label="{{ 'common.save' | translate }}"
          type="submit"
          [disabled]="loading() || blockchainForm.invalid"
          [loading]="loading()"
        >
        </p-button>
      </div>
    </form>
  </p-dialog>

  <!-- Confirmation Dialog -->
  <p-confirmDialog></p-confirmDialog>
</div>
